import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Smartphone, Mail, Video, Cloud, Network, Landmark, Check, Loader2, ArrowRight, PlugZap,
} from "lucide-react";
import { Btn, Overline } from "../ui";
import { useI18n, pickLang } from "../../lib/i18n";

const ICONS = { smartphone: Smartphone, mail: Mail, video: Video, cloud: Cloud, network: Network, landmark: Landmark };

export default function ConnectSource({ sources, onConnect, onContinue }) {
  const { t, lang } = useI18n();
  const [busyId, setBusyId] = useState(null);
  const connected = sources.filter((s) => s.connected).length;

  const connect = async (id) => {
    setBusyId(id);
    await onConnect(id);
    setBusyId(null);
  };

  return (
    <div className="space-y-8" data-testid="screen-source">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <Overline>{t("src.overline")}</Overline>
          <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">{t("src.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">{t("src.sub")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="rounded-lg border border-edge raise px-3.5 py-2">
            <p className="font-mono text-[11px] text-slate-400" data-testid="source-progress">
              {t("src.progress", { n: connected, total: sources.length })}
            </p>
            <div className="mt-1.5 h-1 w-40 rounded-full track">
              <motion.div
                className="h-full rounded-full bg-aegis-cyan"
                animate={{ width: `${(connected / Math.max(1, sources.length)) * 100}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
          </div>
          <Btn variant="solidCyan" onClick={onContinue} disabled={connected === 0} testid="source-continue-btn">
            {t("src.continue")} <ArrowRight size={15} />
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {sources.map((s, i) => {
          const Icon = ICONS[s.icon] || PlugZap;
          const busy = busyId === s.source_id;
          return (
            <motion.div
              key={s.source_id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`source-card-${s.source_id}`}
              className={`flex flex-col rounded-xl border p-6 transition-colors duration-200 ${
                s.connected ? "border-aegis-emerald/30 bg-aegis-emerald/[0.04]" : "border-edge bg-panel/70 hover:border-aegis-cyan/30"
              }`}
            >
              <div className="flex items-start justify-between">
                <span
                  className={`grid place-items-center h-11 w-11 rounded-lg border ${
                    s.connected
                      ? "border-aegis-emerald/40 bg-aegis-emerald/10 text-aegis-emerald"
                      : "border-edge raise text-aegis-cyan"
                  }`}
                >
                  <Icon size={20} strokeWidth={1.6} />
                </span>
                <span className="rounded-md border border-edge raise px-2 py-0.5 font-mono text-[9px] uppercase tracking-overline text-slate-400">
                  {t("src.readonly")}
                </span>
              </div>

              <h3 className="mt-5 font-heading text-[16px] font-medium text-slate-100">{pickLang(s, "name", lang)}</h3>
              <p className="mt-2 flex-1 text-[13px] leading-relaxed text-slate-400">{pickLang(s, "desc", lang)}</p>

              <div className="mt-5 space-y-1 border-t border-edge pt-4">
                <p className="font-mono text-[10px] text-slate-400">
                  {t("src.protocol")} · {s.protocol}
                </p>
                {s.connected && (
                  <p className="font-mono text-[10px] text-aegis-emerald">
                    {s.records?.toLocaleString()} {t("src.records")} · {s.connected_at}
                  </p>
                )}
              </div>

              <div className="mt-5">
                {s.connected ? (
                  <span
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-aegis-emerald/40 bg-aegis-emerald/10 py-2 text-sm font-medium text-aegis-emerald"
                    data-testid={`source-connected-${s.source_id}`}
                  >
                    <Check size={15} /> {t("src.connected")}
                  </span>
                ) : (
                  <Btn
                    variant="cyan"
                    className="w-full"
                    disabled={busy}
                    onClick={() => connect(s.source_id)}
                    testid={`source-connect-${s.source_id}`}
                  >
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
                    {busy ? t("src.connecting") : t("src.connect")}
                  </Btn>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
